import { useEffect, useRef, useState, useMemo } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import OnThisPage from "@components/OnThisPage";
import SubpageLayout from "@components/SubpageLayout";
import SubPageContent from "@components/subPageContent";
import useGetDosing from "@hooks/useGetDosing";
import styled from "styled-components";
import mediaQuery from "@utils/mediaQuery";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
  max-width: 100%;

  @media (max-width: 768px) {
    gap: 24px;
  }
`;


const SectionContainer = styled.div<{ $isHighlighted: boolean }>`
  max-width: 100%;
  overflow-x: auto;

  ${({ $isHighlighted }) =>
    $isHighlighted &&
    `
    background: #fff8e1;
    border: 2px solid var(--color-warning-500);
    border-radius: 8px;
    padding: 24px;
    margin: -8px;
    position: relative;

    &::before {
      content: "📍 You were directed here";
      position: absolute;
      top: -12px;
      left: 24px;
      background: var(--color-warning-500);
      color: #000;
      padding: 4px 12px;
      border-radius: 16px;
      font-size: var(--font-size-base);
      font-weight: var(--font-weight-semibold);
      box-shadow: 0 2px 8px var(--shadow-warning);
    }
  `}

  @media (max-width: 768px) {
    ${({ $isHighlighted }) =>
      $isHighlighted &&
      `
      padding: 16px;
      margin: -4px;

      &::before {
        font-size: var(--font-size-sm);
        padding: 3px 8px;
        left: 16px;
        top: -10px;
      }
    `}
  }
`;

const SearchContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 32px;
  padding: 20px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #c3d4e1;
  position: sticky;
  top: 20px;
  z-index: 10;

  @media (max-width: 768px) {
    top: 10px;
  }
`;

const SearchInput = styled.input`
  padding: 12px 16px;
  border: 1px solid #c3d4e1;
  border-radius: 8px;
  font-size: var(--font-size-xl);
  background-color: white;
  min-width: 250px;
  max-width: 100%;
  flex: 1;
  cursor: text;
  font-family: inherit;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #00558c;
    box-shadow: 0 0 0 3px rgba(0, 85, 140, 0.1);
  }

  &::placeholder {
    color: #a0aec0;
  }

  ${mediaQuery("tablet")`
    min-width: 100%;
  `}
`;

const SearchResults = styled.div`
  font-size: var(--font-size-base);
  color: #505e79;
  font-style: italic;
  line-height: 22.4px;
`;

const SearchRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: flex-start;
`;

const FilterTags = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  width: 100%;
`;

const FilterTag = styled.button<{ $isActive: boolean }>`
  padding: 8px 12px;
  height: 44px;
  border: 1px solid ${({ $isActive }) => ($isActive ? '#00558c' : '#c3d4e1')};
  background: ${({ $isActive }) => ($isActive ? '#00558c' : 'white')};
  color: ${({ $isActive }) => ($isActive ? 'white' : '#505e79')};
  border-radius: 8px;
  font-size: var(--font-size-base);
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  font-family: inherit;

  &:hover {
    background: ${({ $isActive }) => ($isActive ? '#004070' : '#f8f9fa')};
  }

  @media (max-width: 768px) {
    padding: 4px 8px;
    font-size: var(--font-size-xs);
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    gap: 8px;
    margin-bottom: 12px;
  }
`;

const SectionBadge = styled.span<{ $type: 'adult' | 'child' | 'both' }>`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  text-transform: uppercase;
  white-space: nowrap;
  ${({ $type }) => {
    switch ($type) {
      case 'adult':
        return 'background: #e3f2fd; color: #1976d2;';
      case 'child':
        return 'background: #f3e5f5; color: #7b1fa2;';
      default:
        return 'background: #e8f5e8; color: #388e3c;';
    }
  }}

  @media (max-width: 768px) {
    font-size: var(--font-size-2xs);
    padding: 3px 6px;
  }
`;

const ComplexDosing = () => {
  const { dosing: dosingBlocks, error } = useGetDosing();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFilter, setSelectedFilter] = useState<string>("all");
  const [highlightedSection, setHighlightedSection] = useState<string | null>(null);
  const sectionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  // Handle URL parameters for direct linking
  useEffect(() => {
    const medicine = searchParams.get('medicine');
    const age = searchParams.get('age');
    const section = searchParams.get('section');
    
    if (medicine || section) {
      // Find matching section based on medicine name or section parameter
      const targetSection = dosingBlocks?.find(dosing => {
        const title = dosing.title?.toLowerCase() || '';
        if (section) {
          return title.includes(section.toLowerCase());
        }
        if (medicine) {
          return title.includes(medicine.toLowerCase());
        }
        return false;
      });

      if (targetSection) {
        setHighlightedSection(targetSection.title || '');
        
        // Set search filter based on age if provided
        if (age && (age.toLowerCase() === 'child' || age.toLowerCase() === 'adult')) {
          setSelectedFilter(age.toLowerCase());
        }
        
        // Scroll to section after a brief delay
        setTimeout(() => {
          const sectionElement = sectionRefs.current[targetSection.title || ''];
          if (sectionElement) {
            sectionElement.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'start' 
            });
          }
        }, 100);
      }
    }
  }, [dosingBlocks, searchParams]);

  // Clear highlight after 10 seconds
  useEffect(() => {
    if (highlightedSection) {
      const timer = setTimeout(() => {
        setHighlightedSection(null);
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [highlightedSection]);

  // Determine section type (adult/child/both) based on title
  const getSectionType = (title: string): 'adult' | 'child' | 'both' => {
    const lowerTitle = title.toLowerCase();
    const hasAdult = lowerTitle.includes('adult');
    const hasChild = lowerTitle.includes('child') || lowerTitle.includes('paediatric') || lowerTitle.includes('pediatric');
    
    if (hasAdult && hasChild) return 'both';
    if (hasChild) return 'child';
    if (hasAdult) return 'adult';
    
    // Try to infer from common patterns
    if (lowerTitle.includes('neonatal') || lowerTitle.includes('infant')) return 'child';
    
    return 'both'; // Default to both if unclear
  };

  // Filter and search logic
  const filteredDosingBlocks = dosingBlocks?.filter(dosing => {
    const title = dosing.title?.toLowerCase() || '';
    
    // Handle copy - it might be a rich text object, string, or null
    const copy = typeof dosing.copy === 'string' ? dosing.copy : 
                 dosing.copy ? JSON.stringify(dosing.copy) : '';
    
    // Search filter
    const matchesSearch = searchTerm === '' || 
      title.includes(searchTerm.toLowerCase()) ||
      copy.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Age filter
    if (selectedFilter === 'all') {
      return matchesSearch;
    }
    
    const sectionType = getSectionType(dosing.title || '');
    const matchesFilter = sectionType === selectedFilter || sectionType === 'both';
    
    return matchesSearch && matchesFilter;
  });

  // Get unique age categories for filter tags
  const availableFilters = useMemo(() => {
    if (!dosingBlocks) {
      // Provide stable initial filters to prevent layout shift
      return ['all', 'adult', 'child'];
    }
    return ['all', ...new Set(dosingBlocks.map(dosing => {
      const type = getSectionType(dosing.title || '');
      return type === 'both' ? ['adult', 'child'] : [type];
    }).flat())];
  }, [dosingBlocks]);

  const titles = filteredDosingBlocks?.map((dosing) => dosing.title);

  if (error) {
    return <div>{error}</div>;
  }

  return (
    <SubpageLayout
      title="Complex dosing & monitoring protocols"
      subtitle="Find more detailed information on antibiotics for which more complex dosing and monitoring protocols exist."
    >
      <Container>
        <SearchContainer>
          <SearchRow>
            <SearchInput
              type="text"
              placeholder="Search for specific antibiotics or keywords..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />

            <FilterTags>
              {availableFilters.map(filter => (
                <FilterTag
                  key={filter}
                  $isActive={selectedFilter === filter}
                  onClick={() => setSelectedFilter(filter)}
                >
                  {filter === 'all' ? 'All' : filter === 'child' ? 'Paediatric' : 'Adult'}
                </FilterTag>
              ))}
            </FilterTags>
          </SearchRow>

          <SearchResults>
            {dosingBlocks ? (
              <>
                Showing {filteredDosingBlocks?.length || 0} of {dosingBlocks.length} protocols
                {searchTerm && ` for "${searchTerm}"`}
                {selectedFilter !== 'all' && ` in ${selectedFilter === 'child' ? 'paediatric' : selectedFilter} category`}
              </>
            ) : (
              'Loading protocols...'
            )}
          </SearchResults>
        </SearchContainer>

        {titles && <OnThisPage titles={titles} />}
        
        {filteredDosingBlocks?.map((dosing, i) => {
          const sectionType = getSectionType(dosing.title || '');
          const isHighlighted = highlightedSection === dosing.title;
          
          return (
            <SectionContainer
              key={`${dosing.title}__${i}`}
              $isHighlighted={isHighlighted}
              ref={(el) => {
                if (dosing.title) {
                  sectionRefs.current[dosing.title] = el;
                }
              }}
            >
              <SectionHeader>
                <SectionBadge $type={sectionType}>
                  {sectionType === 'both' ? 'All Ages' : 
                   sectionType === 'child' ? 'Paediatric' : 'Adult'}
                </SectionBadge>
              </SectionHeader>
              
              <SubPageContent
                title={dosing.title || ""}
                bodyContent={dosing.copy}
                table={dosing.table}
              />
            </SectionContainer>
          );
        })}

        {filteredDosingBlocks?.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: '#505e79',
            background: '#f8f9fa',
            borderRadius: '8px',
            border: '1px solid #c3d4e1'
          }}>
            <p>No protocols found matching your search criteria.</p>
            <p style={{ fontSize: 'var(--font-size-base)', marginTop: '8px' }}>
              Try adjusting your search term or filter selection.
            </p>
          </div>
        )}
      </Container>
    </SubpageLayout>
  );
};

export default ComplexDosing;